/**
 * Report-only users: accounts that may use the report generator and nothing else. The server
 * answers 403 outside its allowlist; this only keeps them off dead screens. A failed read of the
 * user record redirects nobody (fail open) — the server gate is the real control.
 */
import { ReportRole } from '@/interfaces/database/user-setting';
import api from '@/utils/api';
import { getAuthorization } from '@/utils/authorization-util';

export type ReportAccess = {
  reportOnly: boolean;
  effectiveReportRole: ReportRole | null;
};

const TTL_MS = 60 * 1000;
const OPEN_ACCESS: ReportAccess = {
  reportOnly: false,
  effectiveReportRole: null,
};
const REPORT_ONLY_ROLE = 'report_only';

/** The server's answer (`report_only`, which holds even with no tenant), else the membership's role. */
const isReportOnly = (
  user:
    | { report_only?: boolean | null; tenant_role?: string | null }
    | undefined,
) =>
  typeof user?.report_only === 'boolean'
    ? user.report_only
    : user?.tenant_role === REPORT_ONLY_ROLE;

/**
 * Maps the user record (initialData is {}) and the query's in-flight flag to the access state.
 * Loading only while a fetch is running and no record is in; a settled read without a record
 * (error, non-zero code) fails open so the app still renders.
 */
export const deriveReportAccess = (
  user:
    | {
        id?: string;
        tenant_role?: string | null;
        report_only?: boolean | null;
        effective_report_role?: ReportRole | null;
      }
    | undefined,
  fetching: boolean,
): ReportAccess & { loading: boolean } => ({
  reportOnly: isReportOnly(user),
  effectiveReportRole: user?.effective_report_role ?? null,
  loading: fetching && !user?.id,
});

let cached: { at: number; promise: Promise<ReportAccess> } | null = null;

const coversPath = (path: string, base: string) =>
  path === base || path.startsWith(`${base}/`);

/** Where the report area lives, and so where a report-only user is sent. */
export const REPORT_HOME = '/reportstudio';

/** Pages a report-only user may open: the report area, its old address (which only redirects to
 *  it, so an old link still lands), the no-access page and signing in. */
export const isReportOnlyPathAllowed = (pathname: string): boolean =>
  [REPORT_HOME, '/report', '/no-access', '/login', '/login-next'].some((base) =>
    coversPath(pathname, base),
  );

export const NO_ACCESS_PATH = '/no-access';

/**
 * Where a signed-in user on `pathname` must be sent, or null to stay. A report-only user with no
 * report role (only report roles, on a deployment without reports) has no page at all but the
 * no-access one; any other report-only user stays in the report area.
 */
export const reportOnlyRedirect = (
  access: ReportAccess,
  pathname: string,
): string | null => {
  // The no-access page is for no access only: anyone else on it (a role was granted since) moves on.
  if (
    coversPath(pathname, NO_ACCESS_PATH) &&
    !(access.reportOnly && !access.effectiveReportRole)
  ) {
    return access.reportOnly ? REPORT_HOME : '/';
  }
  if (!access.reportOnly) return null;
  if (!access.effectiveReportRole) {
    return coversPath(pathname, NO_ACCESS_PATH) ? null : NO_ACCESS_PATH;
  }
  return isReportOnlyPathAllowed(pathname) ? null : REPORT_HOME;
};

/** Drop the cached read, so the next loadReportAccess() asks the API (logout, role change). */
export function clearReportAccessCache() {
  cached = null;
}

export function loadReportAccess(): Promise<ReportAccess> {
  const authorization = getAuthorization();
  if (!authorization) {
    return Promise.resolve(OPEN_ACCESS);
  }
  if (cached && Date.now() - cached.at < TTL_MS) {
    return cached.promise;
  }
  const promise = fetch(api.user_info, {
    headers: { Authorization: authorization },
  })
    .then((res) => res.json())
    .then(
      (body): ReportAccess =>
        body?.code === 0
          ? {
              reportOnly: isReportOnly(body.data),
              effectiveReportRole: body.data?.effective_report_role ?? null,
            }
          : OPEN_ACCESS,
    )
    .catch(() => OPEN_ACCESS);
  cached = { at: Date.now(), promise };
  return promise;
}
